// =============================
// File: Disclaimer.js
// =============================

import React, { useEffect as useEffect2 } from "react";
import MKBox2 from "components/MKBox";
import MKTypography2 from "components/MKTypography";
import MKButton2 from "components/MKButton";
import {
  Container as Container2,
  Grid as Grid2,
  Card as Card2,
  Divider as Divider2,
  List as List2,
  ListItem as ListItem2,
  ListItemText as ListItemText2,
  Link as Link2,
} from "@mui/material";

const BackToTop2 = () => (
  <MKBox2 textAlign="right" my={4}>
    <MKButton2 component="a" href="#top" variant="gradient" color="primary">
      Back to top
    </MKButton2>
  </MKBox2>
);

export function DisclaimerPage() {
  const company = "The Best Helper";
  const lastUpdated = "August 30, 2025";

  useEffect2(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    { id: "overview", label: "Overview" },
    { id: "no_warranty", label: "No Warranties" },
    { id: "third_party", label: "Third‑Party Content & Services" },
    { id: "real_estate", label: "Property & Land Disclaimers" },
    { id: "transport", label: "Transport & Drivers" },
    { id: "employment", label: "Household Help & Jobs" },
    { id: "financial", label: "No Financial / Legal Advice" },
    { id: "accuracy", label: "Accuracy & Availability" },
    { id: "security", label: "Security & Viruses" },
    { id: "limitation", label: "Limitation of Liability" },
    { id: "local_law", label: "Local Laws & Compliance" },
    { id: "contact", label: "Contact" },
  ];

  return (
    <MKBox2 id="top">
      <MKBox2
        sx={{
          background: "linear-gradient(135deg, #2d0b00 0%, #4a1f00 50%, #0b1220 100%)",
          color: "#fff",
          py: { xs: 6, md: 10 },
        }}
      >
        <Container2 maxWidth="lg">
          <MKTypography2 variant="h2" color="white" fontWeight="bold" mb={1}>
            Disclaimer
          </MKTypography2>
          <MKTypography2 variant="body1" color="white" sx={{ opacity: 0.9 }}>
            Transparency about how information and introductions on this Platform should be used.
          </MKTypography2>
        </Container2>
      </MKBox2>

      <Container2 maxWidth="lg">
        <Grid2 container spacing={3} sx={{ my: { xs: 2, md: 4 } }}>
          <Grid2 item xs={12} md={4} lg={3}>
            <Card2 sx={{ position: { md: "sticky" }, top: { md: 32 }, p: 2, borderRadius: 3 }}>
              <MKTypography2 variant="h6" fontWeight="bold" mb={1}>
                Table of Contents
              </MKTypography2>
              <Divider2 sx={{ mb: 1 }} />
              <List2 dense>
                {sections.map((s) => (
                  <ListItem2 key={s.id} sx={{ py: 0 }}>
                    <Link2 href={`#${s.id}`} underline="hover">
                      <ListItemText2 primary={s.label} />
                    </Link2>
                  </ListItem2>
                ))}
              </List2>
              <Divider2 sx={{ my: 1 }} />
              <MKTypography2 variant="caption" color="text">
                Last updated: {lastUpdated}
              </MKTypography2>
            </Card2>
          </Grid2>

          <Grid2 item xs={12} md={8} lg={9}>
            <Card2 sx={{ p: { xs: 2, md: 4 }, borderRadius: 4 }}>
              <DSection id="overview" title="1. Overview">
                <p>
                  {company} provides an online interface to discover options and connect with
                  independent providers for housing, vehicles with drivers, land purchases, and
                  household help across East Africa. Information is provided for general guidance
                  only and may not reflect current availability or legal requirements.
                </p>
              </DSection>

              <DSection id="no_warranty" title="2. No Warranties">
                <p>
                  Content and introductions on the Platform are provided “as is.” We make no
                  representations or warranties of any kind, express or implied, including accuracy,
                  completeness, merchantability, fitness for a particular purpose, or
                  non‑infringement.
                </p>
              </DSection>

              <DSection id="third_party" title="3. Third‑Party Content & Services">
                <p>
                  Listings, quotations, and staff profiles may originate from third parties. We do
                  not control, endorse, or guarantee third‑party services, licensing, insurance, or
                  conduct. Contracts are typically between you and the provider.
                </p>
              </DSection>

              <DSection id="real_estate" title="4. Property & Land Disclaimers">
                <ul>
                  <li>
                    Conduct independent inspections, title searches, and legal review before
                    committing.
                  </li>
                  <li>
                    Photos, maps, and descriptions may be illustrative; verify boundaries,
                    utilities, and zoning.
                  </li>
                  <li>
                    Regulatory processes (e.g., transfer or permitting) vary by country and
                    timeline.
                  </li>
                </ul>
              </DSection>

              <DSection id="transport" title="5. Transport & Drivers">
                <ul>
                  <li>
                    Verify vehicle condition, insurance coverage, and driver credentials before use.
                  </li>
                  <li>Road conditions, traffic, and security may affect timing and access.</li>
                </ul>
              </DSection>

              <DSection id="employment" title="6. Household Help & Jobs">
                <p>
                  We are not the employer of record unless explicitly stated. Employers must comply
                  with local labor laws, contracts, wages, and safe working conditions. Candidates
                  must provide truthful credentials.
                </p>
              </DSection>

              <DSection id="financial" title="7. No Financial / Legal Advice">
                <p>
                  Nothing on the Platform constitutes legal, financial, investment, immigration, or
                  tax advice. Seek qualified professional advice tailored to your circumstances.
                </p>
              </DSection>

              <DSection id="accuracy" title="8. Accuracy & Availability">
                <p>
                  Availability, pricing, and terms may change without notice. We do not guarantee
                  uninterrupted access to the Platform or the absence of errors.
                </p>
              </DSection>

              <DSection id="security" title="9. Security & Viruses">
                <p>
                  Although we take reasonable precautions, we do not warrant that the Platform is
                  free from malware, vulnerabilities, or harmful components. Use up‑to‑date security
                  practices.
                </p>
              </DSection>

              <DSection id="limitation" title="10. Limitation of Liability">
                <p>
                  To the maximum extent permitted by law, {company} shall not be liable for indirect
                  or consequential losses, including loss of profits, data, or business, arising
                  from use of the Platform or reliance on third‑party services.
                </p>
              </DSection>

              <DSection id="local_law" title="11. Local Laws & Compliance">
                <p>
                  Users are responsible for understanding and complying with local regulations in
                  Rwanda, Kenya, Uganda, and Tanzania, including property, transport, labor, visa,
                  and tax requirements.
                </p>
              </DSection>

              <DSection id="contact" title="12. Contact">
                <p>Questions about this Disclaimer? Email: legal@thebesthelper.example</p>
              </DSection>

              <BackToTop2 />
            </Card2>
          </Grid2>
        </Grid2>
      </Container2>
    </MKBox2>
  );
}

function DSection({ id, title, children }) {
  return (
    <MKBox2 id={id} mb={4}>
      <MKTypography2 variant="h5" fontWeight="bold" mb={1}>
        {title}
      </MKTypography2>
      <Divider2 sx={{ mb: 2 }} />
      <MKTypography2 variant="body2" color="text" component="div" sx={{ lineHeight: 1.8 }}>
        {children}
      </MKTypography2>
    </MKBox2>
  );
}
